/**
 * A list of records that is a table on a wide screen and a stack of cards on a
 * narrow one.
 *
 * Every table in the app previously solved small screens with
 * `overflow-x-auto` and a `min-w`, which is not a solution: it hides columns
 * behind a sideways swipe most people never try, and nothing else in Bookr
 * scrolls horizontally. Below `sm` each record becomes a card with its columns
 * as label/value rows, so every value stays visible and reachable.
 *
 * The column definition is shared between both layouts, so the two can never
 * drift: adding a column adds it in both places or neither.
 */

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => React.ReactNode;
  align?: "start" | "end";
  /** Use the data (mono) font. For counts, dates and anything compared by eye. */
  numeric?: boolean;
  /** The column that names the record. Becomes the card's heading on a phone. */
  primary?: boolean;
}

export function DataList<T>({
  title,
  count,
  rows,
  rowKey,
  columns,
  empty,
}: {
  title: string;
  count?: number;
  rows: T[];
  rowKey: (row: T) => string;
  columns: Column<T>[];
  empty: string;
}) {
  const primary = columns.find((c) => c.primary) ?? columns[0];
  const rest = columns.filter((c) => c !== primary);

  return (
    <section>
      <h2 className="mb-3 flex items-baseline gap-2 text-sm font-bold text-ink">
        {title}
        {count !== undefined && (
          <span className="font-mono text-xs font-semibold text-muted">{count}</span>
        )}
      </h2>

      {rows.length === 0 ? (
        <p className="rounded-2xl border border-line bg-surface p-8 text-center text-sm text-muted">
          {empty}
        </p>
      ) : (
        <>
          {/* Phone: one card per record, every column on its own row. */}
          <ul className="space-y-3 sm:hidden">
            {rows.map((row) => (
              <li
                key={rowKey(row)}
                className="rounded-2xl border border-line bg-surface p-4 shadow-[var(--shadow-card)]"
              >
                <p className="font-semibold text-ink">{primary.cell(row)}</p>
                <dl className="mt-2 space-y-1">
                  {rest.map((column) => (
                    <div
                      key={column.key}
                      className="flex items-baseline justify-between gap-3"
                    >
                      <dt className="text-sm text-muted">{column.header}</dt>
                      <dd
                        className={`text-sm text-body ${column.numeric ? "font-mono" : ""}`}
                      >
                        {column.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>

          {/* Wide: a real table. */}
          <div className="hidden overflow-hidden rounded-2xl border border-line bg-surface sm:block">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={`px-4 py-2.5 font-semibold ${
                        column.align === "end" ? "text-end" : "text-start"
                      }`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => (
                  <tr key={rowKey(row)}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-2.5 ${
                          column.align === "end" ? "text-end" : "text-start"
                        } ${column.numeric ? "font-mono" : ""} ${
                          column === primary ? "font-medium text-ink" : "text-body"
                        }`}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
